/**
 * Shared domain primitives (STEP 3 of docs/source-analysis.md).
 * Layer: Domain — pure types + id/time helpers, no UI or storage imports.
 */

export type Locale = 'tr' | 'en';

/** Bilingual field. Source-extracted text keeps `tr: null` until manually
 *  translated — never auto-translate (A-05). */
export interface LocalizedString {
  tr: string | null;
  en: string | null;
}

/** Traceability chip: every rule-derived entry points back at its document. */
export interface SourceRef {
  document: string;
  section?: string | null;
  page?: number | null;
}

export interface EntityBase {
  id: string;
  createdAt: string; // ISO-8601
  updatedAt: string; // ISO-8601
}

/** Collision-safe enough for a single-user local app; no external dep. */
export function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function nowIso(): string {
  return new Date().toISOString();
}

export function createEntity<T extends object>(
  base: T,
): T & EntityBase {
  const ts = nowIso();
  return { ...base, id: newId(), createdAt: ts, updatedAt: ts };
}

export function touch<T extends EntityBase>(entity: T): T {
  return { ...entity, updatedAt: nowIso() };
}
